// frontend/src/core/components/NeuralNetworkModule/SettingsNeuralModule.jsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableBody,
  CTableHeaderCell,
  CTableRow,
  CTableDataCell,
  CSpinner,
  CListGroup,
  CListGroupItem,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CAlert,
} from "@coreui/react";
import { CIcon } from "@coreui/icons-react";
import {
  cilPlus,
  cilTrash,
  cilPen,
  cilCopy,
  cilClipboard,
  cilList,
  cilBug,
  cilMagnifyingGlass,
  cilSettings,
} from "@coreui/icons";
import axios from "axios";
import AddConstructions from "./AddConstructions";
import AddDefects from "./AddDefects";
import SettingsAI from "./SettingsAI";
import AddTypes from "./AddTypes";
import AddCombined from "./AddCombined";

const SettingsNeuralModule = ({
  visible,
  onClose,
  onDefectDoubleClick,
  // Новый проп: AiObjectID для фильтрации (вместо Ltype)
  filterAiObjectID = null,
}) => {
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [combinedData, setCombinedData] = useState([]);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showConstructions, setShowConstructions] = useState(false);
  const [showDefects, setShowDefects] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [isSelecting, setIsSelecting] = useState(false);
  const [startRowIndex, setStartRowIndex] = useState(null);
  const [clipboardDataForPaste, setClipboardDataForPaste] = useState(null);
  const [message, setMessage] = useState({
    show: false,
    message: "",
    color: "info",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState([]);
  const tableBodyRef = useRef(null);
  const token = localStorage.getItem("token");
  const [showSettingsAI, setShowSettingsAI] = useState(false);
  const [showEditTypeModal, setShowEditTypeModal] = useState(false);
  const [editTypeData, setEditTypeData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddCombined, setShowAddCombined] = useState(false);
  const [objectIdForAdd, setObjectIdForAdd] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editRecordData, setEditRecordData] = useState(null);

  // Состояние для проверки прав администратора
  const [isAdmin, setIsAdmin] = useState(false);

  // Refs для отмены запросов
  const objectsAbortController = useRef(null);
  const combinedDataAbortController = useRef(null);

  const handleAddTypeSuccess = useCallback(() => {
    // Устанавливаем флаг, что объекты нужно перезагрузить
    loadObjects(); // Перезагружаем объекты сразу
    if (isEditMode) {
      setShowEditTypeModal(false);
      setIsEditMode(false);
      setEditTypeData(null);
    }
  }, [isEditMode]);

  // Проверка прав администратора при открытии
  useEffect(() => {
    if (visible && token) {
      axios
        .get("/api/auth/permissions/", {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          if (response.data?.permissions?.is_admin) {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
        })
        .catch((err) => {
          console.error("Ошибка проверки прав:", err);
          setIsAdmin(false);
        });
    }
  }, [visible, token]);

  // Загрузка объектов с фильтрацией
  const loadObjects = useCallback(async () => {
    // Отменяем предыдущий запрос, если он есть
    if (objectsAbortController.current) {
      objectsAbortController.current.abort();
    }
    const controller = new AbortController();
    objectsAbortController.current = controller;

    setLoadingObjects(true);
    try {
      // Всегда загружаем все объекты
      const response = await axios.get(`/api/neural_network_module/objects/`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });

      let objectsData = [];
      if (Array.isArray(response.data)) {
        objectsData = response.data;
      } else if (response.data && typeof response.data === "object") {
        objectsData = [response.data];
      }

      // --- ЛОГИКА ФИЛЬТРАЦИИ ПО AiObjectID ---
      if (filterAiObjectID !== null) {
        // Фильтруем строго по ObjectID из базы AI
        console.log(`[Settings] Фильтрация по AiObjectID: ${filterAiObjectID}`);
        const filteredObjects = objectsData.filter(
          (obj) => obj.ObjectID === filterAiObjectID,
        );

        if (filteredObjects.length > 0) {
          const targetObject = filteredObjects[0];
          // Устанавливаем в список только его
          setObjects([targetObject]);
          // Автоматически выбираем его
          setSelectedObject(targetObject);
          // Загружаем данные для него
          // ВАЖНО: Вызываем loadCombinedData только если объект действительно сменился
          // или если данные еще не загружены
          // Но так как loadObjects вызывается при открытии, то можно вызвать принудительно
          // loadCombinedData вызовем через useEffect, который следит за selectedObject
        } else {
          console.warn(
            `[Settings] Объект с ObjectID=${filterAiObjectID} не найден в списке объектов нейросети.`,
          );
          setObjects([]); // Ничего не нашли
        }
      } else {
        // Стандартное поведение (показать все)
        const sorted = objectsData.sort((a, b) =>
          a.ObjectName.localeCompare(b.ObjectName),
        );
        setObjects(sorted);
      }
      // --------------------------
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Загрузка объектов отменена");
      } else {
        console.error("Ошибка при загрузке объектов:", error);
        alert("Ошибка при загрузке объектов");
        setObjects([]);
      }
    } finally {
      // Снимаем флаг загрузки только если запрос не был отменен
      if (objectsAbortController.current === controller) {
        setLoadingObjects(false);
      }
    }
  }, [token, filterAiObjectID]);

  // Эффект загрузки при открытии
  useEffect(() => {
    if (visible) {
      loadObjects();
    } else {
      // Сброс при закрытии
      setObjects([]);
      setSelectedObject(null);
      setCombinedData([]);
      setSearchTerm("");
      setSelectedRows(new Set());
      setClipboardDataForPaste(null);
      setMessage({ show: false, message: "", color: "info" });
      setShowDeleteConfirm(false);
      setIdsToDelete([]);
      setShowEditTypeModal(false);
      setIsEditMode(false);
      setEditTypeData(null);
      setIsEditModalOpen(false);
      setEditRecordData(null);
      setLoadingObjects(false);
      setLoadingTable(false);
      
      if (objectsAbortController.current) objectsAbortController.current.abort();
      if (combinedDataAbortController.current) combinedDataAbortController.current.abort();
    }
  }, [visible, loadObjects]);

  // Загрузка данных таблицы
  const loadCombinedData = useCallback(async (objectId) => {
    if (combinedDataAbortController.current) {
        combinedDataAbortController.current.abort();
    }
    const controller = new AbortController();
    combinedDataAbortController.current = controller;

    setLoadingTable(true);
    // Не сбрасываем данные сразу, чтобы не мигало, если просто обновляем
    // setCombinedData([]); 

    try {
      const response = await axios.get(
        `/api/neural_network_module/combinedtable/`,
        {
          params: { object_id: objectId },
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        },
      );
      if (Array.isArray(response.data)) {
        setCombinedData(response.data);
      } else {
        setCombinedData([]);
      }
    } catch (error) {
       if (axios.isCancel(error)) {
            console.log("Загрузка данных таблицы отменена");
       } else {
            console.error(`Ошибка загрузки данных combinedtable:`, error);
            setCombinedData([]);
       }
    } finally {
        if (combinedDataAbortController.current === controller) {
            setLoadingTable(false);
        }
    }
  }, [token]);

  // Эффект для загрузки данных таблицы при выборе объекта
  useEffect(() => {
    if (selectedObject) {
        // Загружаем данные только если выбран объект
        loadCombinedData(selectedObject.ObjectID);
    } else {
        setCombinedData([]);
    }
  }, [selectedObject, loadCombinedData]);


  const handleObjectSelect = (obj) => {
    // Если включен фильтр, запрещаем снимать выбор (опционально, но логично для UI)
    // В данном коде мы просто разрешаем выбор, так как в списке будет только 1 элемент
    if (selectedObject?.ObjectID !== obj.ObjectID) {
        setSelectedObject(obj);
        // loadCombinedData вызывается в useEffect
        
        setSearchTerm("");
        setSelectedRows(new Set());
        setClipboardDataForPaste(null);
        setMessage({ show: false, message: "", color: "info" });
    }
  };

  const handleObjectDoubleClick = async (obj) => {
    // Разрешаем редактирование только если filterAiObjectID не задан И пользователь админ
    if (filterAiObjectID === null && isAdmin) {
      try {
        const response = await axios.get(
          `/api/neural_network_module/objects/`,
          {
            params: { id: obj.ObjectID },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (response.data) {
          setIsEditMode(true);
          setEditTypeData(response.data);
          setShowEditTypeModal(true);
        } else {
          setMessage({
            show: true,
            message: "Не удалось загрузить данные для редактирования.",
            color: "danger",
          });
          setTimeout(
            () => setMessage({ show: false, message: "", color: "info" }),
            3000,
          );
        }
      } catch (error) {
        console.error(
          `Ошибка при загрузке данных объекта ${obj.ObjectID} для редактирования:`,
          error,
        );
        setMessage({
          show: true,
          message:
            "Ошибка при загрузке данных для редактирования: " +
            (error.response?.data?.message || error.message),
          color: "danger",
        });
        setTimeout(
          () => setMessage({ show: false, message: "", color: "info" }),
          5000,
        );
      }
    }
  };

  const normalizeString = (str) => {
    if (!str) return "";
    return str.toString().toLowerCase().trim();
  };

  const matchesSearch = (item, term) => {
    if (!term) return true;
    const normalizedTerm = normalizeString(term);
    const terms = normalizedTerm.split(/\s+/).filter((t) => t.length > 0);
    if (terms.length === 0) return true;
    const constructionText = normalizeString(
      `${item.ClassID} - ${item.ClassName || ""}`,
    );
    const defectText = normalizeString(item.DefectName || "");
    const referenceText = normalizeString(item.AdditionalInfo || "");
    return terms.every(
      (searchWord) =>
        constructionText.includes(searchWord) ||
        defectText.includes(searchWord) ||
        referenceText.includes(searchWord),
    );
  };

  const filteredCombinedData = combinedData.filter((item) =>
    matchesSearch(item, searchTerm),
  );

  const handleRowMouseDown = (index) => {
    setIsSelecting(true);
    setStartRowIndex(index);
    if (!window.event.ctrlKey) {
      setSelectedRows(new Set([index]));
    } else {
      const newSelectedRows = new Set(selectedRows);
      if (newSelectedRows.has(index)) {
        newSelectedRows.delete(index);
      } else {
        newSelectedRows.add(index);
      }
      setSelectedRows(newSelectedRows);
    }
  };

  const handleRowMouseEnter = (index) => {
    if (isSelecting && startRowIndex !== null) {
      const newSelectedRows = new Set();
      const startIndex = Math.min(startRowIndex, index);
      const endIndex = Math.max(startRowIndex, index);
      for (let i = startIndex; i <= endIndex; i++) {
        newSelectedRows.add(i);
      }
      if (window.event && window.event.ctrlKey) {
        selectedRows.forEach((row) => newSelectedRows.add(row));
      }
      setSelectedRows(newSelectedRows);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    setStartRowIndex(null);
  };

  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isSelecting, startRowIndex]);

  const handleCopy = async () => {
    if (selectedRows.size === 0) {
      setMessage({
        show: true,
        message: "Нет выделенных строк для копирования.",
        color: "warning",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        3000,
      );
      return;
    }
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      setMessage({
        show: true,
        message: "Clipboard API недоступен в вашем браузере.",
        color: "warning",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        3000,
      );
      return;
    }
    try {
      const sortedSelectedIndices = Array.from(selectedRows).sort(
        (a, b) => a - b,
      );
      const dataToCopy = sortedSelectedIndices.map(
        (index) => filteredCombinedData[index],
      );
      if (
        dataToCopy.length === 0 ||
        dataToCopy.some((item) => item === undefined)
      ) {
        setMessage({
          show: true,
          message: "Ошибка при подготовке данных.",
          color: "danger",
        });
        setTimeout(
          () => setMessage({ show: false, message: "", color: "info" }),
          3000,
        );
        return;
      }
      const jsonToCopy = JSON.stringify(dataToCopy, null, 2);
      await navigator.clipboard.writeText(jsonToCopy);
      setMessage({
        show: true,
        message: "Данные скопированы в буфер обмена.",
        color: "success",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        3000,
      );
    } catch (err) {
      console.error("[SettingsNeuralModule] Ошибка при копировании: ", err);
      setMessage({
        show: true,
        message: "Не удалось скопировать данные.",
        color: "danger",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        5000,
      );
    }
  };

  const handlePaste = async () => {
    if (!isAdmin) {
        setMessage({
            show: true,
            message: "Недостаточно прав для вставки.",
            color: "warning",
        });
        setTimeout(() => setMessage({ show: false, message: "", color: "info" }), 3000);
        return;
    }

    if (!selectedObject) {
      setMessage({
        show: true,
        message: "Пожалуйста, выберите объект слева.",
        color: "warning",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        3000,
      );
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        setMessage({
          show: true,
          message: "Буфер обмена пуст.",
          color: "warning",
        });
        setTimeout(
          () => setMessage({ show: false, message: "", color: "info" }),
          3000,
        );
        return;
      }
      let dataToPaste;
      try {
        dataToPaste = JSON.parse(text);
      } catch (parseError) {
        setMessage({
          show: true,
          message:
            "Ошибка: Невозможно распарсить данные из буфера обмена как JSON.",
          color: "danger",
        });
        setTimeout(
          () => setMessage({ show: false, message: "", color: "info" }),
          3000,
        );
        return;
      }
      if (!Array.isArray(dataToPaste)) {
        setMessage({
          show: true,
          message: "Ошибка: Данные из буфера обмена не являются массивом JSON.",
          color: "danger",
        });
        setTimeout(
          () => setMessage({ show: false, message: "", color: "info" }),
          3000,
        );
        return;
      }
      if (dataToPaste.length === 0) {
        setMessage({
          show: true,
          message: "Нет данных для вставки.",
          color: "warning",
        });
        setTimeout(
          () => setMessage({ show: false, message: "", color: "info" }),
          3000,
        );
        return;
      }
      setClipboardDataForPaste(dataToPaste);
      await pasteDataToServer(dataToPaste, selectedObject.ObjectID);
    } catch (err) {
      console.error("[SettingsNeuralModule] Ошибка при вставке: ", err);
      if (err.name === "NotAllowedError" || err.name === "SecurityError") {
        setMessage({
          show: true,
          message: "Доступ к буферу обмена запрещен.",
          color: "danger",
        });
      } else {
        setMessage({
          show: true,
          message: "Не удалось прочитать данные из буфера обмена.",
          color: "danger",
        });
      }
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        5000,
      );
    }
  };

  const pasteDataToServer = async (data, targetObjectId) => {
    setMessage({
      show: true,
      message: `Начинаю вставку ${data.length} записей...`,
      color: "info",
    });
    setTimeout(() => {
      if (message.message.startsWith("Начинаю вставку")) {
        setMessage({ show: false, message: "", color: "info" });
      }
    }, 2000);
    let successCount = 0;
    let errorCount = 0;
    const errors = [];
    try {
      for (const item of data) {
        try {
          const payload = {
            ObjectID: targetObjectId,
            ClassID_FirstLevel: item.ClassID_FirstLevel,
            ClassID_SecondLevel: item.ClassID_SecondLevel,
            AdditionalInfo: item.AdditionalInfo ?? null,
            Label: item.Label ?? null,
            DefectName: item.DefectName ?? null,
            DC: item.DC ?? null,
            idDefectTable: item.idDefectTable ?? null,
          };
          const response = await axios.post(
            `/api/neural_network_module/combinedtable/`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (response.status === 200 || response.status === 201) {
            successCount++;
          } else {
            errorCount++;
            errors.push(
              `Ошибка при вставке записи (ID из буфера: ${item.ID || "N/A"}): Статус ${response.status}`,
            );
          }
        } catch (itemError) {
          errorCount++;
          const errorMsg =
            itemError.response?.data?.message ||
            itemError.message ||
            "Неизвестная ошибка";
          errors.push(
            `Ошибка при вставке записи (ID из буфера: ${item.ID || "N/A"}): ${errorMsg}`,
          );
        }
      }
      if (errorCount === 0) {
        setMessage({
          show: true,
          message: `Успешно вставлено ${successCount} записей.`,
          color: "success",
        });
        // Перезагружаем данные таблицы
        loadCombinedData(targetObjectId);
      } else {
        const fullMessage = `Вставлено ${successCount} из ${data.length} записей. Ошибок: ${errorCount}.`;
        console.error("[SettingsNeuralModule] Ошибки вставки:", errors);
        setMessage({ show: true, message: fullMessage, color: "warning" });
      }
    } catch (overallError) {
      console.error(
        "[SettingsNeuralModule] Общая ошибка процесса вставки:",
        overallError,
      );
      setMessage({
        show: true,
        message: `Критическая ошибка при процессе вставки: ${overallError.message}`,
        color: "danger",
      });
    } finally {
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        5000,
      );
    }
  };

  const handleDelete = async () => {
    if (!isAdmin) {
        setMessage({
            show: true,
            message: "Недостаточно прав для удаления.",
            color: "warning",
        });
        setTimeout(() => setMessage({ show: false, message: "", color: "info" }), 3000);
        return;
    }

    if (selectedRows.size === 0) {
      setMessage({
        show: true,
        message: "Нет выделенных строк для удаления.",
        color: "warning",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        3000,
      );
      return;
    }
    const sortedSelectedIndices = Array.from(selectedRows).sort(
      (a, b) => a - b,
    );
    const recordsToDelete = sortedSelectedIndices
      .map((index) => filteredCombinedData[index])
      .filter((item) => item !== undefined);
    const idsToDeleteList = recordsToDelete
      .map((item) => item.ID)
      .filter((id) => id !== undefined);
    if (
      idsToDeleteList.length === 0 ||
      idsToDeleteList.length !== sortedSelectedIndices.length
    ) {
      setMessage({
        show: true,
        message:
          "Ошибка: Не удалось определить ID для всех выделенных записей.",
        color: "danger",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        5000,
      );
      return;
    }
    setIdsToDelete(idsToDeleteList);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAction = async () => {
    setShowDeleteConfirm(false);
    setMessage({
      show: true,
      message: `Начинаю удаление ${idsToDelete.length} записей...`,
      color: "info",
    });
    try {
      let response;
      if (idsToDelete.length === 1) {
        response = await axios.delete(
          `/api/neural_network_module/combinedtable/?id=${idsToDelete[0]}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } else {
        // Исправлено: добавлено 'data:'
        response = await axios.delete(
          `/api/neural_network_module/combinedtable/`,
          {
            data: { ids: idsToDelete },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }
      if (response.status >= 200 && response.status < 300) {
        let deletedMessage =
          response.data?.message || `${idsToDelete.length} записей`;
        if (idsToDelete.length === 1) {
          deletedMessage = response.data?.message || "1 запись";
        }
        setMessage({
          show: true,
          message: `Успешно удалено: ${deletedMessage}.`,
          color: "success",
        });
        setSelectedRows(new Set());
        if (selectedObject) {
          // Перезагружаем данные таблицы после удаления
          loadCombinedData(selectedObject.ObjectID);
        }
      } else {
        setMessage({
          show: true,
          message: `Неожиданный статус ответа: ${response.status}`,
          color: "warning",
        });
        if (selectedObject) {
          loadCombinedData(selectedObject.ObjectID);
        }
      }
    } catch (error) {
      console.error("[SettingsNeuralModule] Ошибка при удалении:", error);
      if (error.response) {
        if (error.response.status === 405) {
          setMessage({
            show: true,
            message:
              "Ошибка: Удаление по этому адресу запрещено (405 Method Not Allowed).",
            color: "danger",
          });
        } else {
          const errorMsg =
            error.response.data?.error ||
            error.response.data?.message ||
            `Ошибка сервера: ${error.response.status}`;
          setMessage({
            show: true,
            message: `Ошибка при удалении: ${errorMsg}`,
            color: "danger",
          });
        }
      } else if (error.request) {
        setMessage({
          show: true,
          message: "Ошибка: Нет ответа от сервера.",
          color: "danger",
        });
      } else {
        setMessage({
          show: true,
          message: `Ошибка при настройке запроса: ${error.message}`,
          color: "danger",
        });
      }
      if (selectedObject) {
        loadCombinedData(selectedObject.ObjectID);
      }
    } finally {
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        5000,
      );
      setIdsToDelete([]);
    }
  };

  const handleAddCombinedClick = () => {
    if (selectedObject) {
      setObjectIdForAdd(selectedObject.ObjectID);
      setShowAddCombined(true);
    } else {
      alert("Пожалуйста, выберите объект перед добавлением записи.");
    }
  };

  const handleAddCombinedSuccess = () => {
    if (selectedObject) {
      loadCombinedData(selectedObject.ObjectID);
    }
  };

  const handleEditClick = async () => {
    if (!isAdmin) {
        setMessage({
            show: true,
            message: "Недостаточно прав для редактирования.",
            color: "warning",
        });
        setTimeout(() => setMessage({ show: false, message: "", color: "info" }), 3000);
        return;
    }

    if (selectedRows.size !== 1) {
      setMessage({
        show: true,
        message: "Пожалуйста, выберите одну строку для редактирования.",
        color: "warning",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        3000,
      );
      return;
    }

    const selectedIndex = Array.from(selectedRows)[0];
    const recordToEdit = filteredCombinedData[selectedIndex];

    if (!recordToEdit || recordToEdit.ID === undefined) {
      setMessage({
        show: true,
        message: "Ошибка: Не удалось определить запись для редактирования.",
        color: "danger",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        3000,
      );
      return;
    }

    try {
      const response = await axios.get(
        `/api/neural_network_module/combinedtable/?id=${recordToEdit.ID}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (
        response.data &&
        typeof response.data === "object" &&
        response.data.ID
      ) {
        setEditRecordData(response.data);
        setIsEditModalOpen(true);
      } else {
        setMessage({
          show: true,
          message: `Ошибка: Запись с ID ${recordToEdit.ID} не найдена.`,
          color: "danger",
        });
        setTimeout(
          () => setMessage({ show: false, message: "", color: "info" }),
          5000,
        );
      }
    } catch (error) {
      console.error(
        `[SettingsNeuralModule] Ошибка при загрузке данных для редактирования записи ${recordToEdit.ID}:`,
        error,
      );
      const errorMsg =
        error.response?.data?.message || error.message || "Неизвестная ошибка";
      setMessage({
        show: true,
        message: `Ошибка при загрузке данных для редактирования: ${errorMsg}`,
        color: "danger",
      });
      setTimeout(
        () => setMessage({ show: false, message: "", color: "info" }),
        5000,
      );
    }
  };

  const modalStyle = {
    display: "flex",
    flexDirection: "column",
  };

  return (
    <>
      <CModal
        alignment="start"
        size="xl"
        visible={visible}
        onClose={onClose}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            Настройки нейросети {filterAiObjectID !== null ? "(Режим выбора)" : ""}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {message.show && (
            <CAlert color={message.color} className="mb-3">
              {message.message}
            </CAlert>
          )}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex flex-wrap gap-2">
              {/* Кнопка "Добавить тип" доступна только если filterAiObjectID не задан и пользователь Админ */}
              {filterAiObjectID === null && isAdmin && (
                <CButton
                  size="sm"
                  onClick={() => {
                    setIsEditMode(false);
                    setEditTypeData(null);
                    setShowEditTypeModal(true);
                  }}
                >
                  <CIcon icon={cilPlus} /> Добавить тип
                </CButton>
              )}

              {/* Кнопка "Добавить" - доступна всем */}
              {isAdmin && (
              <CButton
                size="sm"
                onClick={handleAddCombinedClick}
                disabled={!selectedObject}
              >
                <CIcon icon={cilPlus} /> Добавить
              </CButton>
              )}

              {/* Кнопки редактирования и удаления - только для Админов */}
              {isAdmin && (
                <>
                  <CButton size="sm" onClick={handleDelete}>
                    <CIcon icon={cilTrash} /> Удалить
                  </CButton>
                  <CButton size="sm" onClick={handleEditClick}>
                    <CIcon icon={cilPen} /> Редактировать
                  </CButton>
                </>
              )}

              {/* Кнопки копирования - для всех */}
              <CButton size="sm" onClick={handleCopy}>
                <CIcon icon={cilCopy} /> Копировать
              </CButton>
              
              {/* Кнопка вставки - только для Админов */}
              {isAdmin && (
                <CButton size="sm" onClick={handlePaste}>
                  <CIcon icon={cilClipboard} /> Вставить
                </CButton>
              )}

              {/* Кнопки справочников доступны только в полном режиме (без фильтра) и только Админам */}
              {filterAiObjectID === null && isAdmin && (
                <>
                  <CButton
                    size="sm"
                    onClick={() => setShowConstructions(true)}
                  >
                    <CIcon icon={cilList} /> Конструкции
                  </CButton>
                  <CButton size="sm" onClick={() => setShowDefects(true)}>
                    <CIcon icon={cilBug} /> Дефекты
                  </CButton>
                  <CButton size="sm" onClick={() => setShowSettingsAI(true)}>
                    <CIcon icon={cilSettings} /> Параметры
                  </CButton>
                </>
              )}
            </div>
            <CInputGroup size="sm" style={{ width: "40vh" }}>
              <CInputGroupText>
                <CIcon icon={cilMagnifyingGlass} />
              </CInputGroupText>
              <CFormInput
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Поиск..."
              />
            </CInputGroup>
          </div>
          <CRow>
            {/* Левая панель */}
            <CCol xs={3} className="border-end">
              <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {loadingObjects ? (
                  <div className="text-center p-3">
                    <CSpinner size="sm" />
                    <div>Загрузка объектов...</div>
                  </div>
                ) : objects.length > 0 ? (
                  <CListGroup>
                    {objects.map((obj) => (
                      <CListGroupItem
                        key={obj.ObjectID}
                        action
                        active={
                          selectedObject &&
                          selectedObject.ObjectID === obj.ObjectID
                        }
                        onClick={() => handleObjectSelect(obj)}
                        // Двойной клик для редактирования только в полном режиме и для Админов
                        onDoubleClick={() =>
                          filterAiObjectID === null &&
                          isAdmin &&
                          handleObjectDoubleClick(obj)
                        }
                        style={{
                          cursor:
                            filterAiObjectID === null && isAdmin
                              ? "pointer"
                              : "default",
                        }}
                      >
                        {obj.ObjectName}
                      </CListGroupItem>
                    ))}
                  </CListGroup>
                ) : (
                  <div className="text-muted small p-3">
                    Объекты не найдены
                  </div>
                )}
              </div>
            </CCol>
            <CCol xs={9}>
              <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
                {loadingTable ? (
                  <div className="text-center p-3">
                    <CSpinner size="sm" />
                    <div>Загрузка данных...</div>
                  </div>
                ) : (
                  <CTable striped bordered hover responsive size="sm">
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell style={{ width: "25%" }}>
                          Конструкция
                        </CTableHeaderCell>
                        <CTableHeaderCell style={{ width: "25%" }}>
                          Наименование дефекта
                        </CTableHeaderCell>
                        <CTableHeaderCell style={{ width: "40%" }}>
                          Нормативная ссылка
                        </CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody ref={tableBodyRef}>
                      {filteredCombinedData.length > 0 ? (
                        filteredCombinedData.map((item, index) => {
                          const isRowSelected = selectedRows.has(index);
                          return (
                            <CTableRow
                              key={item.ID || index}
                              onMouseDown={() => handleRowMouseDown(index)}
                              onMouseEnter={() => handleRowMouseEnter(index)}
                              onDoubleClick={() => {
                                // Передаем данные дефекта обратно в AddDefectsModal
                                if (typeof onDefectDoubleClick === "function") {
                                  onDefectDoubleClick(item);
                                }
                              }}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              <CTableDataCell
                                style={{
                                  backgroundColor: isRowSelected
                                    ? "#66A7F2"
                                    : "transparent",
                                  color: isRowSelected ? "#fff" : "inherit",
                                }}
                              >
                                {item.ClassID_FirstLevel} - {item.ClassName || ""}
                              </CTableDataCell>
                              <CTableDataCell
                                style={{
                                  backgroundColor: isRowSelected
                                    ? "#66A7F2"
                                    : "transparent",
                                  color: isRowSelected ? "#fff" : "inherit",
                                }}
                              >
                                {item.DefectName}
                              </CTableDataCell>
                              <CTableDataCell
                                style={{
                                  backgroundColor: isRowSelected
                                    ? "#66A7F2"
                                    : "transparent",
                                  color: isRowSelected ? "#fff" : "inherit",
                                }}
                              >
                                {item.AdditionalInfo}
                              </CTableDataCell>
                            </CTableRow>
                          );
                        })
                      ) : (
                        <CTableRow>
                          <CTableDataCell colSpan="3" className="text-center">
                            {selectedObject
                              ? searchTerm
                                ? "Ничего не найдено по запросу"
                                : "Нет данных для выбранного объекта"
                              : "Выберите объект для отображения данных"}
                          </CTableDataCell>
                        </CTableRow>
                      )}
                    </CTableBody>
                  </CTable>
                )}
              </div>
            </CCol>
          </CRow>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={onClose}>
            Закрыть
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Подтверждение удаления */}
      <CModal
        alignment="start"
        size="lg"
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        style={modalStyle}
      >
        <CModalHeader>
          <CModalTitle>Подтверждение удаления</CModalTitle>
        </CModalHeader>
        <CModalBody>
          Вы уверены, что хотите удалить {idsToDelete.length} запись(и)?
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Отмена
          </CButton>
          <CButton color="danger" onClick={confirmDeleteAction}>
            Удалить
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Вспомогательные модалки */}
      <AddTypes
        visible={showEditTypeModal}
        onClose={() => {
          setShowEditTypeModal(false);
          setIsEditMode(false);
          setEditTypeData(null);
        }}
        onAddSuccess={handleAddTypeSuccess}
        isEditMode={isEditMode}
        editData={editTypeData}
      />
      
      <AddCombined
        isOpen={showAddCombined || isEditModalOpen}
        onClose={() => {
          setShowAddCombined(false);
          setIsEditModalOpen(false);
          setEditRecordData(null);
        }}
        objectId={objectIdForAdd}
        onAddSuccess={handleAddCombinedSuccess}
        isEdit={isEditModalOpen}
        editData={editRecordData}
        onEditSuccess={() => {
          setIsEditModalOpen(false);
          setEditRecordData(null);
          if (selectedObject) {
            loadCombinedData(selectedObject.ObjectID);
          }
          setMessage({
            show: true,
            message: "Запись успешно обновлена.",
            color: "success",
          });
          setTimeout(
            () => setMessage({ show: false, message: "", color: "info" }),
            3000,
          );
        }}
      />
      
      {/* Модалки справочников показываем только в полном режиме */}
      {filterAiObjectID === null && isAdmin && (
        <>
          <AddConstructions
            visible={showConstructions}
            onClose={() => setShowConstructions(false)}
          />
          <AddDefects
            visible={showDefects}
            onClose={() => setShowDefects(false)}
          />
          <SettingsAI
            visible={showSettingsAI}
            onClose={() => setShowSettingsAI(false)}
          />
        </>
      )}
    </>
  );
};

export default SettingsNeuralModule;